import { useMemo } from "react";
import {
  useGetActiveUsersQuery,
  useGetActiveUsersStatsQuery,
  useGetNewUsersQuery,
  useGetLiveViewersQuery,
  useGetTopViewedQuery,
} from "../../store/api/analyticsApi";
import { processDateRangeData } from "../../utils/analytics/processDateRangeData";
import { groupData } from "../../utils/analytics/groupData";

const EMPTY_CHART = {
  xAxis: [],
  yAxis: [],
  percentageChange: 0,
  netValue: 0,
  formattedPercentageChange: "0%",
};

export const useActiveUsers = ({ params, skip, dateRange, isContentPartner = false }) => {
  const { data: activeUsersRaw, isFetching: activeUsersFetching } = useGetActiveUsersQuery(
    params
      ? {
          pubId: params.pub_id,
          channelId: params.channel_id,
          startDate: params.prev_start_date,
          endDate: params.end_date,
        }
      : {},
    { skip: skip || isContentPartner }
  );

  const { data: activeUsersStats, isFetching: activeUsersStatsFetching } = useGetActiveUsersStatsQuery(
    params
      ? {
          pubId: params.pub_id,
          channelId: params.channel_id,
          startDate: params.start_date,
          endDate: params.end_date,
        }
      : {},
    { skip: skip || isContentPartner }
  );

  const { data: newUsersRaw, isFetching: newUsersFetching } = useGetNewUsersQuery(
    params
      ? {
          pubId: params.pub_id,
          startDate: params.prev_start_date,
          endDate: params.end_date,
        }
      : {},
    { skip: skip || isContentPartner }
  );

  const { data: topViewedRaw, isFetching: topViewedFetching } = useGetTopViewedQuery(
    params
      ? {
          pubId: params.pub_id,
          roleId: params.role_id,
          channelId: params.channel_id,
          startDate: params.start_date,
          endDate: params.end_date,
        }
      : {},
    { skip: skip || isContentPartner }
  );

  const { data: liveViewersRaw, isFetching: liveViewersFetching } = useGetLiveViewersQuery(
    params ? { channelId: params.channel_id } : {},
    { skip }
  );

  const activeUsersData = useMemo(() => {
    if (!activeUsersRaw || !params) return EMPTY_CHART;
    const raw = processDateRangeData(
      activeUsersRaw,
      {
        responsePath: "data",
        xAxisKey: "filter_timestamp",
        extractValue: (entry) => entry.user_count,
        reverseValues: true,
      },
      params
    );
    return groupData(raw, dateRange);
  }, [activeUsersRaw, params, dateRange]);

  const newRegistrationsData = useMemo(() => {
    if (!newUsersRaw || !params) return EMPTY_CHART;
    const raw = processDateRangeData(
      newUsersRaw,
      {
        responsePath: "data",
        xAxisKey: "filter_timestamp",
        extractValue: (entry) => +entry.count,
        reverseValues: true,
      },
      params
    );
    return groupData(raw, dateRange);
  }, [newUsersRaw, params, dateRange]);

  const topShows = useMemo(() => {
    if (!topViewedRaw) return [];
    const topTitles = [];

    if (topViewedRaw.shows) {
      topViewedRaw.shows.forEach((item) => {
        topTitles.push({ name: item.title, views: parseInt(item.watch_count, 10), type: "Show" });
      });
    }

    if (topViewedRaw.videos) {
      topViewedRaw.videos.forEach((item) => {
        topTitles.push({ name: item.title, views: parseInt(item.watch_count, 10), type: "Video" });
      });
    }

    if (topViewedRaw.shorts) {
      topViewedRaw.shorts.forEach((item) => {
        topTitles.push({ name: item.title, views: parseInt(item.watch_count, 10), type: "Short" });
      });
    }

    return topTitles
      .sort((a, b) => b.views - a.views)
      .slice(0, 10)
      .map((item, index) => ({ ...item, id: index + 1 }))
      .reverse();
  }, [topViewedRaw]);

  const liveViewers = useMemo(() => liveViewersRaw?.data || 0, [liveViewersRaw]);

  return {
    activeUsersData,
    newRegistrationsData,
    activeUsersStats,
    topShows,
    liveViewers,
    loadingStates: {
      activeUsers: activeUsersFetching,
      activeUsersStats: activeUsersStatsFetching,
      newUsers: newUsersFetching,
      topViewed: topViewedFetching,
      liveViewers: liveViewersFetching,
    },
    loading:
      activeUsersFetching ||
      activeUsersStatsFetching ||
      newUsersFetching ||
      topViewedFetching ||
      liveViewersFetching,
  };
};

export default useActiveUsers;
